import { useMemo, useState } from "react";
import { parseLocalDate } from "../../utils/commandCenter";
import { useStateValue } from "../../context/StateProvider";
import { formatCurrency, formatNumber } from "./../../utils/helper";
import { dealCreditsAgent } from "../../utils/dealCredit";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
export default function RankingsSection() {
  const [{ users, deals }] = useStateValue();

  const [period, setPeriod] = useState("YTD");
  const [volumetype, setvolumetype] = useState("closed");
  const [startingdate, setstartingdate] = useState(
    new Date(new Date().getFullYear(), 0, 1, 0, 0, 0, 0),
  );
  const [endingdate, setendingdate] = useState(
    new Date(new Date().getFullYear(), 11, 31, 23, 59, 59, 999),
    // new Date(new Date().setHours(23, 59, 59, 59)),
  );

  const getDateRange = (period) => {
    const now = new Date();
    now.setHours(23, 59, 59, 999);

    let startDate;
    let endDate;

    switch (period) {
      case "MONTH":
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = now;
        break;

      case "LAST_12_MONTHS":
        // rolling last 12 months
        startDate = new Date(now.getFullYear(), now.getMonth() - 11, 1);
        endDate = now;
        break;

      case "QUARTER":
        // rolling last 3 months (handles year change automatically)
        startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        endDate = now;
        break;

      case "YTD":
        endDate = new Date(now.getFullYear(), 11, 31);
      default:
        startDate = new Date(now.getFullYear(), 0, 1);
    }

    startDate.setHours(0, 0, 0, 0);

    return { startDate, endDate };
  };

  const getstartendDateRange = (startingdate, endingdate) => {
    let startDate = startingdate.setHours(0, 0, 0, 0);
    let endDate = endingdate.setHours(23, 59, 59, 999);

    return { startDate, endDate };
  };

  const normalizeDate = (date) => {
    const d = parseLocalDate(date);
    if (isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const filteredPendingDeals = useMemo(() => {
    const { startDate, endDate } = getstartendDateRange(
      startingdate,
      endingdate,
    );

    return deals.filter((deal) => {
      if (
        deal.dealStage.toLowerCase() != "closed" &&
        deal.dealStage.toLowerCase() != "cancelled" &&
        deal.dealStage.toLowerCase() != "expired"
      ) {
        const closingDate = normalizeDate(deal?.closingdate);
        if (!closingDate) return false;

        return closingDate >= startDate && closingDate <= endDate;
      } else {
        return false;
      }
    });
  }, [deals, startingdate, endingdate]);

  const filteredClosedDeals = useMemo(() => {
    const { startDate, endDate } = getstartendDateRange(
      startingdate,
      endingdate,
    );

    return deals.filter((deal) => {
      if (deal?.dealStage !== "Closed") return false;

      const closingDate = normalizeDate(deal?.closingdate);
      if (!closingDate) return false;

      return closingDate >= startDate && closingDate <= endDate;
    });
  }, [deals, startingdate, endingdate]);

  const filteredAllDeals = useMemo(() => {
    const { startDate, endDate } = getstartendDateRange(
      startingdate,
      endingdate,
    );

    return deals.filter((deal) => {
      const closingDate = normalizeDate(deal?.closingdate);
      if (!closingDate) return false;

      return closingDate >= startDate && closingDate <= endDate;
    });
  }, [deals, startingdate, endingdate]);

  const rankings = useMemo(() => {
    let filteredemployess = users.filter((emp) => {
      return (
        emp.rankingaccess == true &&
        emp.role != "Supervisor" &&
        emp.role != "Accountant"
      );
    });
    const data = filteredemployess.map((employee) => {
      // The leaderboard ranks people by deal count and volume, so a deal may
      // only ever land on ONE person's row. dealCreditsAgent picks that one
      // person: the team lead on an in-house referral, otherwise the agent
      // who worked it. The three lists are already narrowed to the selected
      // date range, so credit is the only question left to ask here.
      const creditedTo = (deal) => dealCreditsAgent(deal, employee.id);

      // 🔹 total deals in selected period (NOT current year)
      const employeeTotalDeals = filteredAllDeals.filter(creditedTo);
      const employeeClosedDeals = filteredClosedDeals.filter(creditedTo);
      const employeePendingDeals = filteredPendingDeals.filter(creditedTo);

      const closedCount = employeeClosedDeals.length;
      const pendingCount = employeePendingDeals.length;

      const totalDealsCount = employeeTotalDeals.length;

      const totalValue = employeeClosedDeals.reduce(
        (sum, d) => sum + (Number(d.offerPrice) || 0),
        0,
      );

      const totalPendingValue = employeePendingDeals.reduce(
        (sum, d) => sum + (Number(d.offerPrice) || 0),
        0,
      );

      const avgValue = closedCount > 0 ? totalValue / closedCount : 0;
      const avgPendingValue =
        pendingCount > 0 ? totalPendingValue / pendingCount : 0;
      const conversionRate =
        totalDealsCount > 0
          ? parseFloat(((closedCount / totalDealsCount) * 100).toFixed(2))
          : 0;

      return {
        id: employee.id,
        name: `${employee.firstname} ${employee.lastname}`,
        dealsCount: closedCount,
        pendingdealsCount: pendingCount,

        totalValue,
        totalPendingValue,
        avgValue,
        avgPendingValue,
        conversion: conversionRate,
      };
    });

    return (
      data
        // .sort((a, b) => b.dealsCount - a.dealsCount)
        .sort((a, b) => {
          // 1️⃣ Primary priority: deals count
          if (volumetype === "closed") {
            if (b.dealsCount !== a.dealsCount) {
              return b.dealsCount - a.dealsCount;
            }

            // 2️⃣ Secondary priority: volume
            return b.totalValue - a.totalValue;
          } else if (volumetype === "pending") {
            if (b.totalPendingValue !== a.totalPendingValue) {
              return b.totalPendingValue - a.totalPendingValue;
            }

            // 2️⃣ Secondary priority: volume
            return b.pendingdealsCount - a.pendingdealsCount;
          } else {
            const totalDealsA =
              (a.dealsCount || 0) + (a.pendingdealsCount || 0);
            const totalDealsB =
              (b.dealsCount || 0) + (b.pendingdealsCount || 0);

            if (totalDealsB !== totalDealsA) {
              return totalDealsB - totalDealsA;
            }

            const totalValueA =
              (a.totalValue || 0) + (a.totalPendingValue || 0);
            const totalValueB =
              (b.totalValue || 0) + (b.totalPendingValue || 0);

            return totalValueB - totalValueA;
          }
        })
        .map((item, index) => ({
          ...item,
          position: index + 1,
          medal:
            index === 0
              ? "gold"
              : index === 1
                ? "silver"
                : index === 2
                  ? "bronze"
                  : "",
        }))
    );
  }, [users, deals, filteredClosedDeals, startingdate, endingdate, volumetype]);

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 mb-10">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 ">
        <div className="flex flex-col ">
          <h2 className="text-lg font-semibold text-gray-800">
            🏆 Top Performers by Volume
          </h2>
          <div className="flex flex-row items-center justify-center">
            <p
              className={`${volumetype == "all" ? "text-[#1E293B] text-[12px]" : "text-gray-500 text-[12px]"} mr-[5px] hover:cursor-pointer hover:underline hover:text-[#1E293B]`}
              onClick={() => {
                setvolumetype("all");
              }}
            >
              All Volume
            </p>
            <p
              className={`${volumetype == "closed" ? "text-[#1E293B] text-[12px]" : "text-gray-500 text-[12px]"} mr-[5px] hover:cursor-pointer hover:underline hover:text-[#1E293B]`}
              onClick={() => {
                setvolumetype("closed");
              }}
            >
              Closed Volume
            </p>
            <p
              className={`${volumetype == "pending" ? "text-[#1E293B] text-[12px]" : "text-gray-500 text-[12px]"} mr-[5px] hover:cursor-pointer hover:underline hover:text-[#1E293B]`}
              onClick={() => {
                setvolumetype("pending");
              }}
            >
              Pending Volume
            </p>
          </div>
        </div>
        <div className="flex flex-row items-center gap-2">
          <div className="flex flex-col">
            <h1>From</h1>

            <DatePicker
              className="border-2 border-gray-300 rounded-lg p-2"
              selected={startingdate}
              onChange={(date) => {
                setstartingdate(new Date(date.setHours(0, 0, 0, 0)));
              }}
            />
          </div>
          <div className="flex flex-col">
            <h1>To</h1>

            <DatePicker
              className="border-2 border-gray-300 rounded-lg p-2"
              selected={endingdate}
              onChange={(date) => {
                setendingdate(new Date(date.setHours(23, 59, 59, 59)));
              }}
            />
          </div>
          {/* <label className="text-gray-700 text-sm">Ranking Period:</label>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            <option value="YTD">YTD</option>
            <option value="LAST_12_MONTHS">Last 12 Months</option>
            <option value="QUARTER">This Quarter</option>
            <option value="MONTH">This Month</option>
          </select> */}
        </div>
      </div>

      {/* Ranking List */}
      <div className="divide-y divide-gray-200">
        {rankings.map((rank) => (
          <div
            key={rank.id}
            className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-4 hover:bg-gray-50 transition px-2 rounded-lg"
          >
            {/* Left */}
            <div className="flex items-center w-full sm:w-auto mb-3 sm:mb-0">
              <div
                className={`w-10 h-10 flex items-center justify-center rounded-full font-bold mr-4 text-white ${
                  rank.medal === "gold"
                    ? "bg-gradient-to-br from-yellow-400 to-amber-500"
                    : rank.medal === "silver"
                      ? "bg-gradient-to-br from-gray-300 to-gray-500"
                      : rank.medal === "bronze"
                        ? "bg-gradient-to-br from-orange-700 to-yellow-700"
                        : "bg-gray-300 text-gray-700"
                }`}
              >
                {rank.position}
              </div>

              <div>
                <div className="font-semibold text-gray-800">{rank.name}</div>
                <div className="text-sm text-gray-600 flex flex-wrap items-center gap-2">
                  {volumetype === "all" ? (
                    <>
                      <span className="">
                        {rank.dealsCount} {"closed deals"}
                      </span>

                      <span>•</span>
                      <span>{formatCurrency(rank.avgValue)} avg</span>
                      <span>•</span>
                      <span className="">
                        {rank.pendingdealsCount} {"pending deals"}
                      </span>

                      <span>•</span>
                      <span>
                        {formatCurrency(rank.avgPendingValue)} avg
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="">
                        {volumetype === "closed"
                          ? rank.dealsCount
                          : rank.pendingdealsCount}{" "}
                        {volumetype === "closed"
                          ? "closed deals"
                          : "pending deals"}
                      </span>

                      <span>•</span>
                      <span>
                        
                        {formatCurrency(
                          volumetype === "closed"
                            ? rank.avgValue
                            : rank.avgPendingValue,
                        )}{" "}
                        avg
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Right */}
            <div className="text-[#1E293B] font-semibold text-lg">
              
              {volumetype === "all"
                ? formatCurrency(rank.totalValue + rank.totalPendingValue)
                : formatCurrency(
                    volumetype === "closed"
                      ? rank.totalValue
                      : rank.totalPendingValue,
                  )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
